import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Download, QrCode, Copy, ExternalLink, BarChart3 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Module-scoped helper so the React Compiler doesn't flag localStorage
// reads inside the component body (immutability rule).
function readAdminSessionToken() {
  let t = localStorage.getItem('session_token');
  if (!t) t = localStorage.getItem('token');
  return t || '';
}

function triggerPngDownload(slug) {
  const t = readAdminSessionToken();
  if (!t) {
    toast.error('Session expired, please re-login');
    return;
  }
  const url = `${API_URL}/api/admin/qr-campaigns/${slug}/png.png?token=${encodeURIComponent(t)}`;
  const a = document.createElement('a');
  a.href = url;
  a.download = `qr-${slug}.png`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function copyToClipboard(text) {
  try {
    navigator.clipboard.writeText(text);
    toast.success('Copied');
  } catch (_) { /* clipboard blocked */ }
}

/**
 * Admin page: postcard QR campaigns.
 *  - Lists every campaign with scan / unique-device / signup / order counts
 *  - Inline "Create campaign" form (auto-mints the multi-use coupon)
 *  - Per-row Download PNG button — fetches `image/png` and triggers a
 *    browser download (uses session_token query so the URL works for
 *    right-click "save as" too)
 *  - Copy QR target URL — the value you encode on the postcard
 *  - Per-row stats drill-down (country + device class breakdown)
 *
 * Auth model: regular admin Bearer header for /admin/qr-campaigns and
 * /admin/qr-campaigns/{slug}/stats. PNG download uses ?token=<session>
 * so a plain <a> can fire it.
 */
export default function AdminQRCodes() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({
    base_slug: '', label_prefix: '',
    suffixes_text: '',  // one per line, optionally "suffix, label"
    coupon_code: 'POSTCARD10', discount_pct: 10,
    coupon_max_uses: 10000, coupon_expires_days: 180,
  });
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [form, setForm] = useState({
    slug: '', label: '', coupon_code: 'POSTCARD10', discount_pct: 10,
    coupon_max_uses: 10000, coupon_expires_days: 180,
  });
  const [submitting, setSubmitting] = useState(false);
  const [statsOpen, setStatsOpen] = useState(null); // slug
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get('/auth/me');
        if (r.data?.role !== 'admin') {
          toast.error('Admin only');
          navigate('/');
          return;
        }
        setUser(r.data);
      } catch (_) {
        navigate('/');
        return;
      }
      await loadCampaigns();
      setLoading(false);
    })();
  }, [navigate]);

  const loadCampaigns = async () => {
    try {
      const r = await axios.get('/admin/qr-campaigns');
      setCampaigns(r.data?.campaigns || []);
    } catch (e) {
      toast.error('Failed to load campaigns');
    }
  };

  const handleCreate = async () => {
    if (!form.slug || !form.label) {
      toast.error('Slug and label are required');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post('/admin/qr-campaigns', form);
      toast.success(`QR campaign ${r.data?.campaign?.slug} created`);
      setCreateOpen(false);
      setForm({ slug: '', label: '', coupon_code: 'POSTCARD10', discount_pct: 10, coupon_max_uses: 10000, coupon_expires_days: 180 });
      await loadCampaigns();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Parses textarea into [{suffix, label?}]. One row per line. Optional
  // comma syntax: `90210, Beverly Hills` → suffix=90210, label="Beverly
  // Hills". Lines with just `90210` use the auto-synthesized label.
  // CSV-style commas inside labels work because we split on the FIRST
  // comma only.
  const parseBulkRows = (text) => {
    return (text || '').split('\n')
      .map((line) => line.trim()).filter(Boolean)
      .map((line) => {
        const idx = line.indexOf(',');
        if (idx === -1) return { suffix: line };
        return { suffix: line.slice(0, idx).trim(), label: line.slice(idx + 1).trim() };
      })
      .filter((r) => r.suffix);
  };

  const handleBulkCreate = async () => {
    const rows = parseBulkRows(bulkForm.suffixes_text);
    if (!bulkForm.base_slug || !bulkForm.label_prefix || rows.length === 0) {
      toast.error('Base slug, label prefix, and at least one suffix are required');
      return;
    }
    setBulkSubmitting(true);
    setBulkResult(null);
    try {
      const r = await axios.post('/admin/qr-campaigns/bulk', {
        base_slug: bulkForm.base_slug,
        label_prefix: bulkForm.label_prefix,
        rows,
        coupon_code: bulkForm.coupon_code,
        discount_pct: bulkForm.discount_pct,
        coupon_max_uses: bulkForm.coupon_max_uses,
        coupon_expires_days: bulkForm.coupon_expires_days,
      });
      setBulkResult(r.data);
      toast.success(`Created ${r.data?.created_count || 0} campaigns${r.data?.skipped_count ? `, skipped ${r.data.skipped_count}` : ''}`);
      await loadCampaigns();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk create failed');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const downloadBulkZip = (baseSlug) => {
    const t = readAdminSessionToken();
    if (!t) { toast.error('Session expired, please re-login'); return; }
    const url = `${API_URL}/api/admin/qr-campaigns/group/${baseSlug}/zip.zip?token=${encodeURIComponent(t)}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = `qr-${baseSlug}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const openStats = async (slug) => {
    setStatsOpen(slug);
    setStats(null);
    try {
      const r = await axios.get(`/admin/qr-campaigns/${slug}/stats`);
      setStats(r.data);
    } catch (e) {
      toast.error('Failed to load stats');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#FF8C00] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-qr-codes-page">
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl sticky top-0 z-20">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <QrCode className="w-6 h-6 text-[#FF8C00]" />
            <h1 className="text-xl font-bold">Postcard QR Codes</h1>
          </div>
          <Button
            variant="outline"
            className="text-sm"
            onClick={() => navigate('/admin')}
            data-testid="qr-back-to-admin"
          >
            ← Back to admin
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 space-y-6">
        <Card className="bg-white/[0.02] border-white/10 p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="max-w-2xl space-y-2">
              <h2 className="text-lg font-semibold">Print + track in 3 steps</h2>
              <ol className="text-sm text-neutral-400 space-y-1 list-decimal list-inside">
                <li>Create a campaign — we mint the QR PNG + the multi-use coupon.</li>
                <li>Download the PNG and drop it into your postcard design (Canva, Photoshop, anything).</li>
                <li>Print the matching URL under the QR (e.g. <code className="bg-white/5 px-1 py-0.5 rounded text-xs">theailedger.ai/qr/postcard-q3</code>) so anyone who can&rsquo;t scan can still type it.</li>
              </ol>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="text-sm"
                onClick={() => { setBulkOpen(true); setCreateOpen(false); }}
                data-testid="qr-bulk-open-btn"
              >
                Bulk generate
              </Button>
              <Button
                className="bg-[#FF8C00] hover:bg-[#FF8C00]/90 text-black font-semibold"
                onClick={() => { setCreateOpen(true); setBulkOpen(false); }}
                data-testid="qr-create-campaign-btn"
              >
                + New campaign
              </Button>
            </div>
          </div>
        </Card>

        {createOpen && (
          <Card className="bg-white/[0.02] border-[#FF8C00]/40 p-6 space-y-4" data-testid="qr-create-form">
            <h3 className="text-base font-semibold">Create QR campaign</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="space-y-1 block">
                <span className="text-xs text-neutral-400">Slug (URL path)</span>
                <Input
                  placeholder="postcard-q4"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                  data-testid="qr-form-slug"
                />
              </label>
              <label className="space-y-1 block">
                <span className="text-xs text-neutral-400">Label (admin-only)</span>
                <Input
                  placeholder="Postcard Q4 Mailing"
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  data-testid="qr-form-label"
                />
              </label>
              <label className="space-y-1 block">
                <span className="text-xs text-neutral-400">Coupon code</span>
                <Input
                  placeholder="POSTCARD10"
                  value={form.coupon_code}
                  onChange={(e) => setForm({ ...form, coupon_code: e.target.value.toUpperCase() })}
                  data-testid="qr-form-coupon-code"
                />
              </label>
              <label className="space-y-1 block">
                <span className="text-xs text-neutral-400">Discount %</span>
                <Input
                  type="number" min="1" max="80"
                  value={form.discount_pct}
                  onChange={(e) => setForm({ ...form, discount_pct: parseInt(e.target.value) || 10 })}
                  data-testid="qr-form-discount-pct"
                />
              </label>
              <label className="space-y-1 block">
                <span className="text-xs text-neutral-400">Coupon max uses</span>
                <Input
                  type="number" min="1"
                  value={form.coupon_max_uses}
                  onChange={(e) => setForm({ ...form, coupon_max_uses: parseInt(e.target.value) || 10000 })}
                />
              </label>
              <label className="space-y-1 block">
                <span className="text-xs text-neutral-400">Coupon expires (days)</span>
                <Input
                  type="number" min="1" max="730"
                  value={form.coupon_expires_days}
                  onChange={(e) => setForm({ ...form, coupon_expires_days: parseInt(e.target.value) || 180 })}
                />
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setCreateOpen(false)} disabled={submitting}>Cancel</Button>
              <Button
                className="bg-[#FF8C00] hover:bg-[#FF8C00]/90 text-black font-semibold"
                onClick={handleCreate}
                disabled={submitting}
                data-testid="qr-form-submit"
              >
                {submitting ? 'Creating…' : 'Create + download PNG'}
              </Button>
            </div>
          </Card>
        )}

        {bulkOpen && (
          <Card className="bg-white/[0.02] border-[#FF8C00]/40 p-6 space-y-4" data-testid="qr-bulk-form">
            <div className="space-y-1">
              <h3 className="text-base font-semibold">Bulk generate QR campaigns</h3>
              <p className="text-xs text-neutral-400">Mint one QR per zip code / per recipient batch in a single click. All children share the same coupon code — only the slug differs, which is what lets you see <em>which mailing list</em> actually converts. Download everything as a ZIP (PNGs + manifest.csv).</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="space-y-1 block">
                <span className="text-xs text-neutral-400">Base slug (URL prefix)</span>
                <Input
                  placeholder="postcard-q4"
                  value={bulkForm.base_slug}
                  onChange={(e) => setBulkForm({ ...bulkForm, base_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                  data-testid="qr-bulk-base-slug"
                />
              </label>
              <label className="space-y-1 block">
                <span className="text-xs text-neutral-400">Label prefix</span>
                <Input
                  placeholder="Postcard Q4"
                  value={bulkForm.label_prefix}
                  onChange={(e) => setBulkForm({ ...bulkForm, label_prefix: e.target.value })}
                  data-testid="qr-bulk-label-prefix"
                />
              </label>
            </div>
            <label className="space-y-1 block">
              <span className="text-xs text-neutral-400">Suffixes — one per line. Optional &ldquo;suffix, label&rdquo; syntax.</span>
              <textarea
                rows={6}
                value={bulkForm.suffixes_text}
                onChange={(e) => setBulkForm({ ...bulkForm, suffixes_text: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-sm font-mono focus:border-[#FF8C00]/50 outline-none"
                placeholder={'90210, Beverly Hills CA\n10001, Manhattan NY\n60601, Chicago IL\n94110, San Francisco CA'}
                data-testid="qr-bulk-suffixes"
              />
              <span className="text-[11px] text-neutral-500">
                Final slug = <code className="bg-white/5 px-1 rounded">{bulkForm.base_slug || 'base-slug'}-90210</code>. Tracked separately in stats.
              </span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <label className="space-y-1 block">
                <span className="text-xs text-neutral-400">Shared coupon</span>
                <Input
                  value={bulkForm.coupon_code}
                  onChange={(e) => setBulkForm({ ...bulkForm, coupon_code: e.target.value.toUpperCase() })}
                />
              </label>
              <label className="space-y-1 block">
                <span className="text-xs text-neutral-400">Discount %</span>
                <Input type="number" min="1" max="80" value={bulkForm.discount_pct}
                  onChange={(e) => setBulkForm({ ...bulkForm, discount_pct: parseInt(e.target.value) || 10 })} />
              </label>
              <label className="space-y-1 block">
                <span className="text-xs text-neutral-400">Max uses</span>
                <Input type="number" min="1" value={bulkForm.coupon_max_uses}
                  onChange={(e) => setBulkForm({ ...bulkForm, coupon_max_uses: parseInt(e.target.value) || 10000 })} />
              </label>
              <label className="space-y-1 block">
                <span className="text-xs text-neutral-400">Expires (days)</span>
                <Input type="number" min="1" max="730" value={bulkForm.coupon_expires_days}
                  onChange={(e) => setBulkForm({ ...bulkForm, coupon_expires_days: parseInt(e.target.value) || 180 })} />
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => { setBulkOpen(false); setBulkResult(null); }} disabled={bulkSubmitting}>Cancel</Button>
              <Button
                className="bg-[#FF8C00] hover:bg-[#FF8C00]/90 text-black font-semibold"
                onClick={handleBulkCreate}
                disabled={bulkSubmitting}
                data-testid="qr-bulk-submit"
              >
                {bulkSubmitting ? 'Generating…' : 'Generate batch'}
              </Button>
            </div>

            {bulkResult && (
              <div className="border-t border-white/10 pt-4 space-y-3" data-testid="qr-bulk-result">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-sm">
                    <span className="text-emerald-400 font-semibold">{bulkResult.created_count}</span> created · {bulkResult.skipped_count} skipped · base <code className="text-xs bg-white/5 px-1 rounded">{bulkResult.base_slug}</code>
                  </div>
                  <Button
                    className="bg-[#FF8C00] hover:bg-[#FF8C00]/90 text-black font-semibold"
                    onClick={() => downloadBulkZip(bulkResult.base_slug)}
                    data-testid="qr-bulk-download-zip"
                  >
                    <Download className="w-4 h-4 mr-2" /> Download ZIP (PNGs + manifest)
                  </Button>
                </div>
                {bulkResult.skipped_count > 0 && (
                  <details className="text-xs text-neutral-400">
                    <summary className="cursor-pointer">Skipped {bulkResult.skipped_count} row(s)</summary>
                    <ul className="mt-2 space-y-1 list-disc list-inside">
                      {bulkResult.skipped.map((s, i) => (
                        <li key={i}><code className="bg-white/5 px-1 rounded">{s.suffix}</code> — {s.reason}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </Card>
        )}

        <div className="space-y-3">
          {campaigns.length === 0 ? (
            <Card className="bg-white/[0.02] border-white/10 p-8 text-center text-sm text-neutral-400">
              No QR campaigns yet. Hit <span className="text-[#FF8C00] font-semibold">+ New campaign</span> to mint your first one.
            </Card>
          ) : campaigns.map((c) => (
            <Card key={c.slug} className="bg-white/[0.02] border-white/10 p-5" data-testid={`qr-row-${c.slug}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="space-y-2 flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold">{c.label}</h3>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${c.active ? 'bg-emerald-500/15 text-emerald-300' : 'bg-neutral-500/20 text-neutral-400'}`}>
                      {c.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-xs text-neutral-500 font-mono">{c.target_url}</p>
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    <span className="bg-white/5 px-2 py-1 rounded-full">code <code className="font-bold">{c.coupon_code}</code></span>
                    <span className="bg-white/5 px-2 py-1 rounded-full">{c.discount_pct}% off</span>
                    {c.group_slug && (
                      <span className="bg-[#FF8C00]/15 text-[#FF8C00] px-2 py-1 rounded-full font-semibold">
                        batch · {c.group_slug}
                      </span>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-5 gap-3 text-center">
                  <Stat label="Scans" value={c.scan_count || 0} />
                  <Stat label="Unique" value={c.unique_device_count || 0} />
                  <Stat label="Signups" value={c.signup_count || 0} />
                  <Stat label="Orders" value={c.order_count || 0} />
                  <Stat
                    label="Revenue"
                    value={c.revenue_cents_attributed
                      ? `$${(c.revenue_cents_attributed / 100).toFixed(0)}`
                      : '$0'}
                    valueClass={c.revenue_cents_attributed > 0 ? 'text-emerald-400' : 'text-white'}
                  />
                </div>
                <div className="flex flex-col gap-2 min-w-[180px]">
                  <Button
                    className="bg-[#FF8C00] hover:bg-[#FF8C00]/90 text-black font-semibold text-sm"
                    onClick={() => triggerPngDownload(c.slug)}
                    data-testid={`qr-download-${c.slug}`}
                  >
                    <Download className="w-4 h-4 mr-2" /> Download PNG
                  </Button>
                  <Button variant="outline" className="text-sm" onClick={() => copyToClipboard(c.target_url)}>
                    <Copy className="w-4 h-4 mr-2" /> Copy URL
                  </Button>
                  <Button variant="ghost" className="text-sm" onClick={() => openStats(c.slug)} data-testid={`qr-stats-${c.slug}`}>
                    <BarChart3 className="w-4 h-4 mr-2" /> View stats
                  </Button>
                  {c.group_slug && (
                    <Button
                      variant="ghost"
                      className="text-xs text-[#FF8C00] hover:bg-[#FF8C00]/10"
                      onClick={() => downloadBulkZip(c.group_slug)}
                      data-testid={`qr-batch-zip-${c.slug}`}
                    >
                      ↓ Whole batch ZIP
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>

        {statsOpen && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6" onClick={() => setStatsOpen(null)}>
            <Card
              className="bg-[#0a0a0a] border-white/10 p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="qr-stats-modal"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">{statsOpen} — stats</h3>
                <Button variant="ghost" size="sm" onClick={() => setStatsOpen(null)}>✕</Button>
              </div>
              {!stats ? (
                <div className="text-sm text-neutral-400">Loading…</div>
              ) : (
                <div className="space-y-4 text-sm">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <Stat label="Total scans" value={stats.total_scans} />
                    <Stat label="Unique devices" value={stats.unique_device_count} />
                    <Stat label="Signups" value={stats.signup_count} />
                    <Stat label="Orders" value={stats.order_count} />
                    <Stat
                      label="Revenue attributed"
                      value={stats.revenue_cents_attributed
                        ? `$${(stats.revenue_cents_attributed / 100).toFixed(2)}`
                        : '$0'}
                      valueClass={stats.revenue_cents_attributed > 0 ? 'text-emerald-400' : 'text-white'}
                    />
                  </div>
                  {/* Conversion-rate inline tile. Scans → orders is the single
                      most important QR campaign metric — tells you which
                      mailing list ACTUALLY converts vs. just gets opened. */}
                  {stats.total_scans > 0 && (
                    <div className="bg-white/[0.03] border border-white/10 rounded-lg p-3 text-sm">
                      <span className="text-neutral-400">Conversion rate (scans → orders):</span>{' '}
                      <span className={`font-bold ${
                        stats.order_count / stats.total_scans >= 0.05 ? 'text-emerald-400' :
                        stats.order_count / stats.total_scans >= 0.01 ? 'text-sky-400' :
                        'text-neutral-300'
                      }`}>
                        {((stats.order_count / stats.total_scans) * 100).toFixed(2)}%
                      </span>
                      <span className="text-neutral-500 text-xs ml-2">
                        ({stats.order_count} of {stats.total_scans})
                      </span>
                    </div>
                  )}
                  <Breakdown title="By country" data={stats.by_country} />
                  <Breakdown title="By device class" data={stats.by_device_class} />
                  <div>
                    <h4 className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Recent scans</h4>
                    <div className="border border-white/10 rounded-lg max-h-60 overflow-y-auto">
                      {(stats.recent_scans || []).map((s) => (
                        <div key={s.scan_id} className="px-3 py-2 border-b border-white/5 text-xs flex items-center justify-between">
                          <span className="text-neutral-400">{new Date(s.scanned_at).toLocaleString()}</span>
                          <span className="text-neutral-500">{s.device_class} · {s.country || '??'}</span>
                        </div>
                      ))}
                      {(!stats.recent_scans || stats.recent_scans.length === 0) && (
                        <div className="px-3 py-4 text-xs text-neutral-500 text-center">No scans yet</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, valueClass = 'text-white' }) {
  return (
    <div className="bg-white/[0.03] rounded-lg px-3 py-2 min-w-[64px]">
      <div className={`text-lg font-bold ${valueClass}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
    </div>
  );
}

function Breakdown({ title, data }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return null;
  return (
    <div>
      <h4 className="text-xs uppercase tracking-widest text-neutral-500 mb-2">{title}</h4>
      <div className="flex flex-wrap gap-2">
        {entries.map(([k, v]) => (
          <span key={k} className="bg-white/5 px-2 py-1 rounded-full text-xs">
            <span className="font-bold mr-1">{v}</span>
            <span className="text-neutral-400">{k}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
